<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  channelLabel,
  channelDotHex,
} from 'dashboard/components-next/Contacts/contactChannel';
import { relativeTime } from 'dashboard/components-next/Contacts/contactTime';

// "Conversaciones" tab: one card with a row per conversation (channel dot,
// last message, channel · agent, status pill, when). Clicking opens the
// conversation in the inbox.
const props = defineProps({
  contact: { type: Object, required: true },
});

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const conversationsByContact = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const stripHtml = text => String(text || '').replace(/<[^>]+>/g, '');

// La fila se titula con el último mensaje público: una nota privada es
// contenido interno de la conversación y no debe leerse desde aquí.
const previewOf = conversation => {
  const candidates = [
    conversation.lastPublicMessage,
    conversation.lastNonActivityMessage,
    conversation.messages?.[conversation.messages.length - 1],
  ];
  return candidates.find(message => message && !message.private)?.content;
};

const rows = computed(() =>
  [...(conversationsByContact.value(props.contact.id) || [])]
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .map(conversation => {
      const lastMessage = previewOf(conversation);
      const channel = conversation.meta?.channel;
      return {
        id: conversation.id,
        title:
          stripHtml(lastMessage) ||
          t('CONTACTS_LAYOUT.DETAILS.CONVERSATIONS_TAB.UNTITLED', {
            id: conversation.id,
          }),
        subtitle: [
          channelLabel(channel),
          conversation.meta?.assignee?.name ||
            t('CONTACTS_LAYOUT.DETAILS.SUMMARY.UNASSIGNED'),
        ]
          .filter(Boolean)
          .join(' · '),
        dot: channelDotHex(channel),
        status: conversation.status,
        statusLabel: t(
          `CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${conversation.status}.TEXT`
        ),
        when: relativeTime(conversation.timestamp, locale.value),
      };
    })
);

const openConversation = id => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: id },
  });
};
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <section
    v-else-if="rows.length"
    class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
  >
    <button
      v-for="row in rows"
      :key="row.id"
      type="button"
      class="flex items-center w-full gap-3 px-[18px] py-3 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
      @click="openConversation(row.id)"
    >
      <span
        class="size-[7px] rounded-full flex-shrink-0"
        :style="{ backgroundColor: row.dot }"
      />
      <span class="flex flex-col gap-0.5 min-w-0 flex-1">
        <span class="text-[13px] font-semibold truncate text-n-slate-12">
          {{ row.title }}
        </span>
        <span class="text-xs text-n-slate-10 truncate">{{ row.subtitle }}</span>
      </span>
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
        :class="
          row.status === 'open'
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{ row.statusLabel }}
      </span>
      <span class="text-xs text-n-slate-9 whitespace-nowrap flex-shrink-0">
        {{ row.when }}
      </span>
    </button>
  </section>
  <p
    v-else
    class="px-6 py-10 m-0 text-sm leading-6 text-center rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-11"
  >
    {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
  </p>
</template>
